import { ChangeDetectionStrategy, Component, computed, inject, signal, Signal, WritableSignal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatList } from '@angular/material/list';
import { Router } from '@angular/router';
import { CepStateService } from '@libs/shared/common';
import { CatalogSearchSuggestionModel, CatalogSearchSuggestionsResultModel } from '@libs/shop/models';
import { CatalogSearchRepositoryService } from '@libs/shared/services';
import { firstValueFrom } from 'rxjs';
import { SearchSuggestionCardComponent } from '../../catalog-search-suggestions/search-suggestion-card/search-suggestion-card.component';
import { SearchOverlayHeaderComponent } from '../../catalog-search-suggestions/search-overlay-header/search-overlay-header.component';

@Component({
  selector: 'ui-search-suggestion-list',
  templateUrl: './search-suggestions-list.component.html',
  styleUrls: ['./search-suggestions-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatList,
    SearchOverlayHeaderComponent,
    SearchSuggestionCardComponent,
  ],
})
export class SearchSuggestionsListComponent {
  public suggestions: WritableSignal<CatalogSearchSuggestionModel[]> = signal([]);
  public initialSearchText: Signal<string> = computed(() => this.cepStateService.searchParams().q ?? '');

  private readonly dialogRef: MatDialogRef<SearchSuggestionsListComponent> = inject<MatDialogRef<SearchSuggestionsListComponent>>(MatDialogRef);
  private readonly catalogSearchRepository: CatalogSearchRepositoryService = inject(CatalogSearchRepositoryService);
  private readonly router: Router = inject(Router);
  private readonly cepStateService: CepStateService = inject(CepStateService);

  private currentSearchText: string = '';

  public constructor() {
    void this.onSearchTextChanged(this.initialSearchText());
  }

  public closeDialog(): void {
    this.dialogRef.close();
    this.updateSearchText(this.currentSearchText);
  }

  public async onSearchTextChanged(text: string): Promise<void> {
    this.currentSearchText = text;

    if (text.length >= 3) {
      const suggestionsResult: CatalogSearchSuggestionsResultModel = await firstValueFrom(this.catalogSearchRepository.getSuggestions(text));

      this.suggestions.set(suggestionsResult.suggestions);
    } else {
      this.suggestions.set([]);
    }
  }

  public onEnter(searchText: string): void {
    if (searchText.length <= 0) {
      this.updateSearchText(searchText);

      return;
    }

    void this.router.navigate(['search'], {
      queryParams: { q: searchText, page: 0 },
    });

    this.currentSearchText = searchText;
    this.closeDialog();
  }

  private updateSearchText(searchText: string): void {
    this.cepStateService.updateSearchParams({
      category: undefined,
      q: searchText,
    });
    this.currentSearchText = searchText;
  }
}
